import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import AdminDashboardPage from './AdminDashboardPage';
import AdminReportsPage from './AdminReportsPage';
import AdminCollegeEntryPage from './AdminCollegeEntryPage';
import AdminAptitudeTopicsPage from './AdminAptitudeTopicsPage';
import AdminQuestionManagerPage from './AdminQuestionManagerPage';
import AdminSubmittedQuestionsPage from './AdminSubmittedQuestionsPage';
import AdminCreateTestPage from './AdminCreateTestPage';
import AdminTestHistoryPage from './AdminTestHistoryPage';
import AdminTestHistoryEditPage from './AdminTestHistoryEditPage';
import AdminStudentResultsPage from './AdminStudentResultsPage';
import AdminInstructionsPage from './AdminInstructionsPage';
import AdminSettingsPage from './AdminSettingsPage';
import AdminProfilePage from './AdminProfilePage';
import Logo from '../../assets/images/logos/logo2.png';
import './admin.css';

const AdminLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const userMenuRef = useRef(null);

    const pageTitle = (() => {
        if (location.pathname.includes('/admin/users')) {
            return 'Users';
        }
        if (location.pathname.includes('/admin/college-entry')) {
            return 'College Entry';
        }
        if (location.pathname.includes('/admin/reports')) {
            return 'Reports';
        }
        if (location.pathname.includes('/admin/aptitude-topics')) {
            return 'Aptitude Topics';
        }
        if (location.pathname.includes('/admin/question-manager')) {
            return 'Question Manager';
        }
        if (location.pathname.includes('/admin/submitted-questions')) {
            return 'Submitted Questions';
        }
        if (location.pathname.includes('/admin/test-history/edit')) {
            return 'Edit Test';
        }
        if (location.pathname.includes('/admin/test-history')) {
            return 'Test History';
        }
        if (location.pathname.includes('/admin/student-results')) {
            return 'Student Results';
        }
        if (location.pathname.includes('/admin/instructions')) {
            return 'Instructions';
        }
        if (location.pathname.includes('/admin/settings')) {
            return 'Settings';
        }
        if (location.pathname.includes('/admin/profile')) {
            return 'Profile';
        }
        return 'Dashboard';
    })();

    const handleLogout = async () => {
        const result = await Swal.fire({
            title: 'Are you trying to logout?',
            text: 'You will need to login again to continue.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, logout',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#1e40af',
            cancelButtonColor: '#d33',
        });

        if (!result.isConfirmed) {
            return;
        }

        localStorage.removeItem('guest_user_role');
        localStorage.removeItem('guest_access_token');
        localStorage.removeItem('guest_refresh_token');
        localStorage.removeItem('guest_admin_user');
        sessionStorage.removeItem('guest_user_role');
        sessionStorage.removeItem('guest_access_token');
        sessionStorage.removeItem('guest_refresh_token');
        await Swal.fire({
            title: 'Logged Out',
            text: 'You have successfully logged out.',
            icon: 'success',
            timer: 1500,
            showConfirmButton: false
        });
        navigate('/login');
    };

    useEffect(() => {
        const handleDocumentClick = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setIsUserMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleDocumentClick);
        return () => document.removeEventListener('mousedown', handleDocumentClick);
    }, []);

    return (
        <div className="admin-ui-shell">
            <aside className="admin-ui-sidebar">
                <div className="admin-brand-wrap">
                    <img src={Logo} alt="MindMetric" className="admin-brand-logo" />
                </div>

                <nav className="admin-ui-nav">
                    <NavLink to="/admin/dashboard?role=admin" className="admin-ui-nav-item">
                        <span className="admin-ui-nav-icon">▦</span>
                        <span>Dashboard</span>
                    </NavLink>
                    <NavLink to="/admin/college-entry?role=admin" className="admin-ui-nav-item">
                        <span className="admin-ui-nav-icon">▣</span>
                        <span>College Entry</span>
                    </NavLink>
                    <NavLink to="/admin/aptitude-topics?role=admin" className="admin-ui-nav-item">
                        <span className="admin-ui-nav-icon">◍</span>
                        <span>Aptitude Topics</span>
                    </NavLink>
                    <NavLink to="/admin/question-manager?role=admin" className="admin-ui-nav-item">
                        <span className="admin-ui-nav-icon">👤</span>
                        <span>Question Manager</span>
                    </NavLink>
                    <NavLink to="/admin/submitted-questions?role=admin" className="admin-ui-nav-item">
                        <span className="admin-ui-nav-icon">✉</span>
                        <span>Submitted Questions</span>
                    </NavLink>
                    <NavLink to="/admin/test-history?role=admin" className="admin-ui-nav-item">
                        <span className="admin-ui-nav-icon">🧪</span>
                        <span>Test History</span>
                    </NavLink>
                    <NavLink to="/admin/student-results?role=admin" className="admin-ui-nav-item">
                        <span className="admin-ui-nav-icon">📊</span>
                        <span>Student Results</span>
                    </NavLink>
                    <NavLink to="/admin/instructions?role=admin" className="admin-ui-nav-item">
                        <span className="admin-ui-nav-icon">≡</span>
                        <span>Instructions</span>
                    </NavLink>
                    <NavLink to="/admin/reports?role=admin" className="admin-ui-nav-item">
                        <span className="admin-ui-nav-icon">📈</span>
                        <span>Reports</span>
                    </NavLink>
                    <NavLink to="/admin/settings?role=admin" className="admin-ui-nav-item">
                        <span className="admin-ui-nav-icon">⚙</span>
                        <span>Settings</span>
                    </NavLink>
                </nav>
            </aside>

            <main className="admin-ui-main">
                <header className="admin-ui-topbar">
                    <div className="admin-ui-title-wrap">
                        <span className="admin-ui-menu-icon">☰</span>
                        <h2>{pageTitle}</h2>
                    </div>

                    <div className="admin-ui-actions">
                        <div className="admin-ui-user-menu" ref={userMenuRef}>
                            <button
                                type="button"
                                className="admin-ui-user-toggle"
                                aria-label="Open user menu"
                                onClick={() => setIsUserMenuOpen((prev) => !prev)}
                            >
                                <div className="admin-ui-avatar">U</div>
                            </button>
                            {isUserMenuOpen ? (
                                <div className="admin-ui-user-dropdown" role="menu">
                                    <button
                                        type="button"
                                        className="admin-ui-user-dropdown-item"
                                        onClick={() => {
                                            setIsUserMenuOpen(false);
                                            navigate('/admin/profile?role=admin');
                                        }}
                                    >
                                        Profile
                                    </button>
                                    <button
                                        type="button"
                                        className="admin-ui-user-dropdown-item admin-ui-user-dropdown-item-danger"
                                        onClick={handleLogout}
                                    >
                                        Logout
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </header>

                <section className="admin-ui-content">
                    <Routes>
                        <Route path="dashboard" element={<AdminDashboardPage />} />
                        <Route path="college-entry" element={<AdminCollegeEntryPage />} />
                        <Route path="reports" element={<AdminReportsPage />} />
                        <Route path="aptitude-topics" element={<AdminAptitudeTopicsPage />} />
                        <Route path="question-manager" element={<AdminQuestionManagerPage />} />
                        <Route path="submitted-questions" element={<AdminSubmittedQuestionsPage />} />
                        <Route path="create-test" element={<AdminCreateTestPage />} />
                        <Route path="test-history" element={<AdminTestHistoryPage />} />
                        <Route path="test-history/edit/:testId" element={<AdminTestHistoryEditPage />} />
                        <Route path="student-results" element={<AdminStudentResultsPage />} />
                        <Route path="instructions" element={<AdminInstructionsPage />} />
                        <Route path="settings" element={<AdminSettingsPage />} />
                        <Route path="profile" element={<AdminProfilePage />} />
                        <Route path="*" element={<Navigate to="/admin/dashboard?role=admin" replace />} />
                    </Routes>
                </section>
            </main>
        </div>
    );
};

export default AdminLayout;
